/**
 * Redstone Relay: a small, deterministic, Minecraft-*inspired* signal puzzle. Not a redstone
 * simulator — the rules below are ours and are shown to players in the rules panel.
 *
 * Rules (ruleset v1):
 *  - Signals are on/off. Time moves in ticks; every run starts with every gate off.
 *  - Sources, and levers set to on, power all four neighbours.
 *  - Wires that touch (up/down/left/right) form one net. A net is on when anything powers any of
 *    its cells; an on net powers every cell next to it.
 *  - Gates face a direction and power only the cell in front of them. They use the inputs from
 *    the previous tick, so each gate adds one tick of delay:
 *      repeater: on when the cell behind it powers it
 *      not:      on when the cell behind it does NOT power it
 *      and / or: inputs from its left and right sides
 *  - A lamp is lit when something powers it. A run ends when the state stops changing (win if
 *    every goal lamp is lit and every forbidden lamp is dark) or when it keeps changing
 *    (flickering circuit: no win). Runs are bounded by MAX_TICKS.
 */

export const RULESET = 1;
export const MAX_TICKS = 64;
export const MAX_SIZE = 10;

export type Dir = 0 | 1 | 2 | 3; // north, east, south, west
export const DX = [0, 1, 0, -1] as const;
export const DY = [-1, 0, 1, 0] as const;

export type PieceKind = 'wire' | 'repeater' | 'not' | 'and' | 'or';
export const PIECES: PieceKind[] = ['wire', 'repeater', 'not', 'and', 'or'];
export const DIRECTIONAL: ReadonlySet<PieceKind> = new Set(['repeater', 'not', 'and', 'or']);

/** Fixed cells set by the level. */
export type FixedCell =
  | { kind: 'source' }
  | { kind: 'lever'; id: string }
  | { kind: 'lamp'; goal: boolean }
  | { kind: 'block' }
  | { kind: 'piece'; piece: PieceKind; dir: Dir };

export interface RedstoneLevel {
  id: string;
  version: number;
  title: string;
  /** One short sentence: what to do. */
  goal: string;
  hints: string[];
  width: number;
  height: number;
  /** Key "x,y". */
  fixed: Record<string, FixedCell>;
  /** Pieces the player may place, with how many of each. */
  supply: Partial<Record<PieceKind, number>>;
  /** Gold medal: solved using at most this many placed pieces. */
  budget: number;
  /** A known solution (tests prove it wins; never sent as a hint). */
  solution: Board;
}

export interface Placement {
  x: number;
  y: number;
  piece: PieceKind;
  dir: Dir;
}

export interface Board {
  placements: Placement[];
  levers: Record<string, boolean>;
}

type Cell =
  | { kind: 'empty' }
  | { kind: 'source' }
  | { kind: 'lever'; on: boolean }
  | { kind: 'lamp'; goal: boolean }
  | { kind: 'block' }
  | { kind: 'wire' }
  | { kind: 'gate'; piece: Exclude<PieceKind, 'wire'>; dir: Dir };

export interface TickState {
  /** Cells receiving power this tick, "x,y". */
  powered: string[];
  /** Lamp cells lit this tick. */
  lit: string[];
}

export interface RunResult {
  ok: true;
  ticks: TickState[];
  outcome: 'win' | 'wrong-lamps' | 'flicker';
  piecesUsed: number;
}

export type BoardError = { ok: false; error: string };

const key = (x: number, y: number) => `${x},${y}`;

/** Checks the board against the level: bounds, free cells, supply, known levers. */
export function buildGrid(level: RedstoneLevel, board: Board): Cell[][] | BoardError {
  if (board.placements.length > level.width * level.height)
    return { ok: false, error: 'too many pieces' };
  const grid: Cell[][] = Array.from({ length: level.height }, () =>
    Array.from({ length: level.width }, (): Cell => ({ kind: 'empty' })),
  );
  const levers = new Set<string>();
  for (const [k, f] of Object.entries(level.fixed)) {
    const [x, y] = k.split(',').map(Number) as [number, number];
    if (f.kind === 'lever') levers.add(f.id);
    grid[y]![x] =
      f.kind === 'lever'
        ? { kind: 'lever', on: board.levers[f.id] === true }
        : f.kind === 'piece'
          ? f.piece === 'wire'
            ? { kind: 'wire' }
            : { kind: 'gate', piece: f.piece, dir: f.dir }
          : f;
  }
  for (const id of Object.keys(board.levers))
    if (!levers.has(id)) return { ok: false, error: 'unknown lever' };
  const used: Partial<Record<PieceKind, number>> = {};
  for (const p of board.placements) {
    if (!Number.isInteger(p.x) || !Number.isInteger(p.y)) return { ok: false, error: 'bad cell' };
    if (p.x < 0 || p.y < 0 || p.x >= level.width || p.y >= level.height)
      return { ok: false, error: 'outside the board' };
    if (!PIECES.includes(p.piece) || ![0, 1, 2, 3].includes(p.dir))
      return { ok: false, error: 'unknown piece' };
    if (grid[p.y]![p.x]!.kind !== 'empty') return { ok: false, error: 'cell is taken' };
    used[p.piece] = (used[p.piece] ?? 0) + 1;
    if (used[p.piece]! > (level.supply[p.piece] ?? 0))
      return { ok: false, error: `not enough ${p.piece} pieces` };
    grid[p.y]![p.x] =
      p.piece === 'wire' ? { kind: 'wire' } : { kind: 'gate', piece: p.piece, dir: p.dir };
  }
  return grid;
}

/** Runs the board from the zero state. Pure and deterministic. */
export function run(level: RedstoneLevel, board: Board): RunResult | BoardError {
  if (level.width > MAX_SIZE || level.height > MAX_SIZE)
    return { ok: false, error: 'board too big' };
  const grid = buildGrid(level, board);
  if (!Array.isArray(grid)) return grid;
  const W = level.width;
  const H = level.height;
  const at = (x: number, y: number): Cell | undefined => grid[y]?.[x];

  // Wire nets: flood fill once.
  const netOf = new Map<string, number>();
  const nets: string[][] = [];
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      if (at(x, y)!.kind !== 'wire' || netOf.has(key(x, y))) continue;
      const id = nets.length;
      const cells: string[] = [];
      const stack: [number, number][] = [[x, y]];
      netOf.set(key(x, y), id);
      while (stack.length) {
        const [cx, cy] = stack.pop()!;
        cells.push(key(cx, cy));
        for (let d = 0; d < 4; d++) {
          const nx = cx + DX[d]!;
          const ny = cy + DY[d]!;
          if (at(nx, ny)?.kind === 'wire' && !netOf.has(key(nx, ny))) {
            netOf.set(key(nx, ny), id);
            stack.push([nx, ny]);
          }
        }
      }
      nets.push(cells);
    }

  const gates: { x: number; y: number; piece: Exclude<PieceKind, 'wire'>; dir: Dir }[] = [];
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const c = at(x, y)!;
      if (c.kind === 'gate') gates.push({ x, y, piece: c.piece, dir: c.dir });
    }
  let gateOut = gates.map(() => false);

  /** One tick: which cells receive power, given the gate outputs from the previous tick. */
  const step = () => {
    const recv = new Set<string>();
    const give = (x: number, y: number) => {
      if (x >= 0 && y >= 0 && x < W && y < H) recv.add(key(x, y));
    };
    for (let y = 0; y < H; y++)
      for (let x = 0; x < W; x++) {
        const c = at(x, y)!;
        if (c.kind === 'source' || (c.kind === 'lever' && c.on))
          for (let d = 0; d < 4; d++) give(x + DX[d]!, y + DY[d]!);
      }
    gates.forEach((g, i) => gateOut[i] && give(g.x + DX[g.dir]!, g.y + DY[g.dir]!));
    // A net is on if any of its cells receives power; it then powers its neighbours.
    const netOn = nets.map((cells) => cells.some((c) => recv.has(c)));
    nets.forEach((cells, i) => {
      if (!netOn[i]) return;
      for (const c of cells) {
        recv.add(c);
        const [x, y] = c.split(',').map(Number) as [number, number];
        for (let d = 0; d < 4; d++) give(x + DX[d]!, y + DY[d]!);
      }
    });
    const powers = (fromX: number, fromY: number, toX: number, toY: number) => {
      // Does cell (from) power its neighbour (to)?
      const c = at(fromX, fromY);
      if (!c) return false;
      if (c.kind === 'source' || (c.kind === 'lever' && c.on)) return true;
      if (c.kind === 'wire') return netOn[netOf.get(key(fromX, fromY))!]!;
      if (c.kind === 'gate') {
        const i = gates.findIndex((g) => g.x === fromX && g.y === fromY);
        return gateOut[i]! && fromX + DX[c.dir]! === toX && fromY + DY[c.dir]! === toY;
      }
      return false;
    };
    const next = gates.map((g) => {
      const side = (rel: number) => {
        const d = (g.dir + rel) % 4;
        return powers(g.x + DX[d]!, g.y + DY[d]!, g.x, g.y);
      };
      switch (g.piece) {
        case 'repeater':
          return side(2);
        case 'not':
          return !side(2);
        case 'and':
          return side(1) && side(3);
        case 'or':
          return side(1) || side(3);
      }
    });
    const lit: string[] = [];
    for (let y = 0; y < H; y++)
      for (let x = 0; x < W; x++)
        if (at(x, y)!.kind === 'lamp' && recv.has(key(x, y))) lit.push(key(x, y));
    return { recv, lit, next };
  };

  const ticks: TickState[] = [];
  const seen = new Set<string>();
  let outcome: RunResult['outcome'] = 'flicker';
  for (let t = 0; t < MAX_TICKS; t++) {
    const cur = gateOut.map(Number).join('');
    const s = step();
    ticks.push({ powered: [...s.recv].sort(), lit: s.lit });
    const next = s.next.map(Number).join('');
    if (next === cur) {
      // Settled: the next tick would look exactly like this one.
      outcome = lampsOk(level, s.lit) ? 'win' : 'wrong-lamps';
      break;
    }
    seen.add(cur);
    if (seen.has(next)) break; // back to an earlier state: it flickers forever
    gateOut = s.next;
  }
  return { ok: true, ticks, outcome, piecesUsed: board.placements.length };
}

function lampsOk(level: RedstoneLevel, lit: string[]) {
  const on = new Set(lit);
  return Object.entries(level.fixed).every(([k, f]) => f.kind !== 'lamp' || on.has(k) === f.goal);
}

/** Medal for a winning run: gold within the level's piece budget. */
export const medalOf = (level: RedstoneLevel, r: RunResult) =>
  r.outcome !== 'win' ? null : r.piecesUsed <= level.budget ? 'gold' : 'solved';
